import type { UserBillingData } from "../../api/admin-revenue";
import { t } from "../../i18n";

export function UserSummary({
  userId,
  billing,
  onGrant,
  onResync,
  onDeveloperCredit,
}: {
  userId: string;
  billing: UserBillingData;
  onGrant: () => void;
  onResync: () => void;
  onDeveloperCredit: () => void;
}): React.JSX.Element {
  const tokenBalance = billing.token_balance;
  const subscriptionUsage = tokenBalance
    ? `${tokenBalance.subscription_tokens_used} / ${tokenBalance.subscription_tokens_limit}`
    : "-";
  const topupRemaining = tokenBalance
    ? String(tokenBalance.topup_tokens_remaining)
    : "-";

  return (
    <div style={s.card}>
      <div style={s.cardGrid}>
        <Field label={t("billing.user")} value={userId} />
        <Field
          label={t("billing.plan")}
          value={billing.subscription?.plan_type ?? "-"}
        />
        <Field
          label={t("billing.status")}
          value={billing.subscription?.status ?? "-"}
        />
        <Field
          label={t("billing.subscription_usage")}
          value={subscriptionUsage}
        />
        <Field label={t("billing.topup_remaining")} value={topupRemaining} />
      </div>
      <div style={s.cardActions}>
        <button type="button" onClick={onGrant} style={s.actionBtn}>
          {t("billing.grant_tokens")}
        </button>
        <button type="button" onClick={onResync} style={s.actionBtnDanger}>
          {t("billing.resync_entitlement")}
        </button>
        <button type="button" onClick={onDeveloperCredit} style={s.actionBtn}>
          {t("billing.developer_credit")}
        </button>
      </div>
    </div>
  );
}

export function GrantAmountInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div style={s.grantRow}>
      <label htmlFor="grant-amount" style={s.grantLabel}>
        {t("billing.token_amount_label")}
      </label>
      <input
        id="grant-amount"
        type="number"
        min={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={s.grantInput}
      />
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div>
      <span style={s.fieldLabel}>{label}</span>
      <span style={s.fieldValue}>{value}</span>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  card: {
    padding: 16,
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    marginBottom: 24,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 12,
  },
  cardActions: { display: "flex", gap: 8 },
  actionBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
  actionBtnDanger: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-danger)",
    color: "#fff",
  },
  grantRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  grantLabel: { fontSize: 13, color: "var(--color-text-secondary)" },
  grantInput: {
    width: 120,
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 14,
  },
  fieldLabel: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    display: "block",
    marginBottom: 2,
  },
  fieldValue: { fontSize: 14, fontWeight: 500 },
};
